import React from 'react';
import { isLearnOrUnsetType } from 'utils/tenantType';
import { useTenantIndex } from './useTenantIndex';
import { useInstanceInfoMap } from './useInstanceInfoMap';

export interface TenantNode {
  id: string;
  tenantType?: string | null;
  tenantChildren?: string[] | null;
  region?: string | null;
}

/**
 * Data export of one Learn tenant:
 * - the id of its LearnDms tenant when it has data export
 * - false when it has no data export
 * - null when the lookup failed
 */
export type LearnDataExport = string | false | null;

/** Keyed by Learn tenant id. Missing key means the lookup is still running. */
export type DataExportStatusMap = Record<string, LearnDataExport>;

/** Region where the resources of a Learn site live, and the tenant type it was taken from */
export interface ResourceRegion {
  region?: string;
  /** Tenant type the region was taken from, for example LearnInstance */
  source: string;
}

/** Keyed by Learn tenant id. Missing key means the lookup is still running. */
export type ResourceRegionMap = Record<string, ResourceRegion>;

export interface LearnDataExportResult {
  dataExport: DataExportStatusMap;
  resourceRegions: ResourceRegionMap;
}

/**
 * Region to compare with the Illuminate region for a tenant in a data sharing list.
 * Learn tenants use the region of their resources, other tenants their own region.
 * Undefined while the resource region of a Learn tenant is still loading.
 */
export const getComparedRegion = (
  tenant: TenantNode,
  resourceRegions: ResourceRegionMap,
): ResourceRegion | undefined => {
  if (isLearnOrUnsetType(tenant.tenantType)) return resourceRegions[tenant.id];
  return { region: tenant.region ?? undefined, source: tenant.tenantType ?? 'Tenant' };
};

const isType = (tenant: TenantNode, type: string) =>
  tenant.tenantType?.toLowerCase() === type.toLowerCase();

/**
 * Checks for every Learn tenant (or tenant without a type) in the list whether it has data export.
 * A Learn tenant has data export when it has a LearnDms child,
 * or when its LearnInstance has a LearnDms child.
 *
 * It also finds the region where the resources of the Learn site live. This is the region of the
 * LearnInstance, then the region of the LearnDms, and the region of the Learn tenant
 * when it has neither. The region of the Learn tenant itself is not always set correctly.
 *
 * The children are taken from the shared tenant list, so no request is made per tenant.
 * Only a Learn tenant with more than one LearnInstance (for example during a migration) needs
 * a request, to find out which instance is the current one, the same way the tenant list does.
 */
export const useLearnDataExport = (tenants?: TenantNode[]): LearnDataExportResult => {
  const { index, error } = useTenantIndex();

  const getInstances = React.useCallback(
    (learnTenant: TenantNode) =>
      (index?.getChildren(learnTenant.id) ?? []).filter((c) => isType(c, 'LearnInstance')),
    [index],
  );

  // A string key keeps the list stable between renders, so the instances are not fetched again
  const multiInstanceKey = React.useMemo(
    () =>
      (tenants ?? [])
        .filter((t) => isLearnOrUnsetType(t.tenantType) && getInstances(t).length > 1)
        .map((t) => t.id)
        .join(','),
    [tenants, getInstances],
  );
  const multiInstanceIds = React.useMemo(
    () => (multiInstanceKey ? multiInstanceKey.split(',') : []),
    [multiInstanceKey],
  );
  const { data: currentInstances } = useInstanceInfoMap(multiInstanceIds);

  return React.useMemo(() => {
    const result: LearnDataExportResult = { dataExport: {}, resourceRegions: {} };
    // Nothing is known while the tenant list is loading
    if (!tenants?.length || (!index && !error)) return result;

    const learnTenantRegion = (learnTenant: TenantNode): ResourceRegion => ({
      region: learnTenant.region ?? undefined,
      source: 'Learn',
    });

    tenants
      .filter((t) => isLearnOrUnsetType(t.tenantType))
      .forEach((learnTenant) => {
        if (!index) {
          // The tenant list failed to load
          result.dataExport[learnTenant.id] = null;
          result.resourceRegions[learnTenant.id] = learnTenantRegion(learnTenant);
          return;
        }

        const instances = getInstances(learnTenant);
        let instance: TenantNode | undefined = instances[0];
        if (instances.length > 1) {
          // Still loading which instance is the current one
          if (!(learnTenant.id in currentInstances)) return;
          // When the request failed, the first instance is used
          const currentId = currentInstances[learnTenant.id]?.TenantId;
          instance = instances.find((i) => i.id === currentId) ?? instances[0];
        }

        const dms =
          index.getChildren(learnTenant.id).find((c) => isType(c, 'LearnDms')) ??
          (instance && index.getChildren(instance.id).find((c) => isType(c, 'LearnDms')));

        let resourceRegion = learnTenantRegion(learnTenant);
        if (instance?.region) {
          resourceRegion = { region: instance.region, source: 'LearnInstance' };
        } else if (dms?.region) {
          resourceRegion = { region: dms.region, source: 'LearnDms' };
        }

        result.dataExport[learnTenant.id] = dms?.id ?? false;
        result.resourceRegions[learnTenant.id] = resourceRegion;
      });

    return result;
  }, [tenants, index, error, getInstances, currentInstances]);
};
